/**
 * The network transport. Every CSA HTTP call lives here and nowhere else.
 *
 * Screens do not import this module. They import csaApi.js, which decides
 * between here and the local store depending on whether the audit has been
 * provisioned for offline use. Keeping the transport separate is what made
 * that swap possible without touching a single screen.
 */

import axios from "../../../APIs/axios";
import { config } from "../../../config";

const BASE = `${config.PATH}/api/csa`;

/* ------------------------------------------------------------------ */
/* errors                                                              */
/* ------------------------------------------------------------------ */

/**
 * Server errors reach the screens in one shape, so no screen has to know how
 * axios nests things. `status` is what the screens branch on — 409 in
 * particular, which is never a failure to show as "something went wrong": it
 * means someone else's edit is already there (design §6).
 */
export class CsaError extends Error {
  constructor(message, status, body) {
    super(message);
    this.name = "CsaError";
    this.status = status || 0;
    this.body = body || null;
    this.isConflict = status === 409;
    this.isOffline = status === 0;
  }
}

function wrap(err) {
  if (err instanceof CsaError) return err;
  const res = err && err.response;
  if (!res) {
    return new CsaError(
      "No connection to the server. Your work is still on this device.",
      0,
      null
    );
  }
  const body = res.data || {};
  return new CsaError(body.message || "Something went wrong", res.status, body);
}

/* ------------------------------------------------------------------ */
/* transport                                                           */
/* ------------------------------------------------------------------ */

async function read(path, params) {
  try {
    const r = await axios.get(BASE + path, params ? { params } : undefined);
    return r.data;
  } catch (err) {
    throw wrap(err);
  }
}

/** Every write goes through here, so one place decides how errors are shaped. */
async function mutate(method, path, body) {
  try {
    const r = await axios({ method, url: BASE + path, data: body });
    return r.data;
  } catch (err) {
    throw wrap(err);
  }
}

/**
 * A client-side id that is stable before the row reaches the server.
 * Not a UUID — `uuid` is already a dependency, but this needs no entropy
 * guarantee beyond "unique within one auditor's device".
 */
export function newLocalId() {
  return Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
}

/* ------------------------------------------------------------------ */
/* clients                                                             */
/* ------------------------------------------------------------------ */

export const listOrganizations = (q) => read("/organizations", q ? { q } : null);
export const listSites = (orgId) => read(`/organizations/${orgId}/sites`);
export const createOrganization = (org_name, sites) =>
  mutate("post", "/organizations", { org_name, sites: sites || [] });
export const createSite = (orgId, site_name) =>
  mutate("post", `/organizations/${orgId}/sites`, { site_name });

/* ------------------------------------------------------------------ */
/* framework                                                           */
/* ------------------------------------------------------------------ */

/**
 * The framework is the same 163 questions for every audit on a version, so it
 * is fetched once per session rather than per screen. This cache is also the
 * natural place for the offline layer to hand back the stored copy instead.
 */
let frameworkCache = null;

export async function getFramework(version) {
  if (!version && frameworkCache) return frameworkCache;
  const fw = await read("/framework", version ? { version } : null);
  if (!version) frameworkCache = fw;
  return fw;
}

export function clearFrameworkCache() {
  frameworkCache = null;
}

/* ------------------------------------------------------------------ */
/* the audit                                                           */
/* ------------------------------------------------------------------ */

export const createAudit = (fields) =>
  mutate("post", "/audit", { local_id: newLocalId(), ...fields });

/**
 * My audits. The query string is passed through as built by the caller —
 * status, q, limit, offset. The server caps limit and rejects an unknown
 * status, so there is nothing to validate twice here.
 */
export const listAudits = (qs) => read("/audits" + (qs ? "?" + qs : ""));

export const getAudit = (id) => read(`/audit/${id}`);
export const getRollup = (id) => read(`/audit/${id}/rollup`);
export const getReadiness = (id) => read(`/audit/${id}/readiness`);

/**
 * Autosave one answer.
 *
 * `row_version` is what the caller last read. Omitting it disables the check
 * and lets a stale tab overwrite a colleague's edit, so screens must pass it.
 * A mismatch throws a CsaError with `isConflict` set and the current value in
 * `err.body.current` — enough to show the auditor both and let them choose.
 */
export const saveAnswer = (id, answer) => mutate("post", `/audit/${id}/answer`, answer);

/** Fill the blanks in one sub-pillar or element with Yes (design §7e). */
export const bulkYes = (id, level, level_value) =>
  mutate("post", `/audit/${id}/bulk-yes`, { level, level_value });

/* ------------------------------------------------------------------ */
/* scope                                                               */
/* ------------------------------------------------------------------ */

export const addExclusion = (id, exclusion) =>
  mutate("post", `/audit/${id}/exclusion`, exclusion);
export const removeExclusion = (id, exclusionId) =>
  mutate("delete", `/audit/${id}/exclusion/${exclusionId}`);

/* ------------------------------------------------------------------ */
/* the note pad                                                        */
/* ------------------------------------------------------------------ */

/** `forCode` orders the list for the write-up screen; omit it for the pad. */
export const listNotes = (id, forCode) =>
  read(`/audit/${id}/notes`, forCode ? { for: forCode } : null);

export const addNote = (id, body, sub_pillar) =>
  mutate("post", `/audit/${id}/notes`, { local_id: newLocalId(), body, sub_pillar });

/** Copies the note into the question's comment — it does not link (§7e). */
export const useNote = (id, noteId, question_code) =>
  mutate("post", `/audit/${id}/notes/${noteId}/use`, { question_code });

export const deleteNote = (id, noteId) => mutate("delete", `/audit/${id}/notes/${noteId}`);

/* ------------------------------------------------------------------ */
/* off-checklist observations                                          */
/* ------------------------------------------------------------------ */

export const listExtras = (id) => read(`/audit/${id}/extras`);
export const addExtra = (id, extra) =>
  mutate("post", `/audit/${id}/extras`, { local_id: newLocalId(), ...extra });
export const updateExtra = (id, extraId, patch) =>
  mutate("put", `/audit/${id}/extras/${extraId}`, patch);
export const deleteExtra = (id, extraId) =>
  mutate("delete", `/audit/${id}/extras/${extraId}`);

/* ------------------------------------------------------------------ */
/* the write-up pass                                                   */
/* ------------------------------------------------------------------ */

/** Gaps only — No and Partial — with the auditor's own notes beside each. */
export const getGaps = (id) => read(`/audit/${id}/gaps`);

/* ------------------------------------------------------------------ */
/* workflow                                                            */
/* ------------------------------------------------------------------ */

export const submitAudit = (id, opts) => mutate("post", `/audit/${id}/submit`, opts || {});
export const reviewAudit = (id, opts) => mutate("post", `/audit/${id}/review`, opts || {});
export const returnAudit = (id, reason) => mutate("post", `/audit/${id}/return`, { reason });

/**
 * The auditor takes their own submitted audit back.
 *
 * No reason, unlike returnAudit — that one is a reviewer's message to the
 * auditor, and this one is addressed to nobody. The server records the
 * transition either way, so the history still shows who reopened it and when.
 */
export const reopenAudit = (id) => mutate("post", `/audit/${id}/reopen`, {});

/* ------------------------------------------------------------------ */
/* offline (design §9)                                                 */
/* ------------------------------------------------------------------ */

/** Replay the outbox. Returns one acknowledgement per operation sent. */
export const syncAudit = (id, device_id, operations) =>
  mutate("post", `/audit/${id}/sync`, { device_id, operations });

/** Record that this device holds the audit. Warns on take-over; never refuses. */
export const claimAudit = (id, device_id) =>
  mutate("post", `/audit/${id}/claim`, { device_id });

/* ------------------------------------------------------------------ */
/* photos (design §9)                                                  */
/* ------------------------------------------------------------------ */

export const listPhotos = (id) => read(`/audit/${id}/photos`);

/**
 * Upload one photo.
 *
 * Deliberately not through `mutate`: this carries bytes, so it needs
 * multipart rather than JSON. The browser sets its own multipart boundary, so
 * Content-Type is left alone — setting it by hand omits the boundary and the
 * server sees an empty body.
 *
 * Keyed server-side on (audit_id, local_id), so retrying after a dropped
 * connection overwrites the same object instead of leaving an orphan.
 */
export async function uploadPhoto(id, { local_id, blob, thumb, question_code, captured_at }) {
  const form = new FormData();
  form.append("local_id", local_id);
  if (captured_at) form.append("captured_at", captured_at);
  if (question_code) form.append("question_code", question_code);
  form.append("photo", blob, local_id + ".jpg");
  if (thumb) form.append("thumb", thumb, local_id + "_t.jpg");

  try {
    const r = await axios.post(BASE + `/audit/${id}/photos`, form);
    return r.data;
  } catch (err) {
    throw wrap(err);
  }
}

export const linkPhoto = (id, local_id, question_code) =>
  mutate("post", `/audit/${id}/photos/${local_id}/links`, { question_code });

export const unlinkPhoto = (id, local_id, question_code) =>
  mutate("delete", `/audit/${id}/photos/${local_id}/links/${encodeURIComponent(question_code)}`);

export const deletePhoto = (id, local_id) =>
  mutate("delete", `/audit/${id}/photos/${local_id}`);

/* ------------------------------------------------------------------ */
/* the report (§7a, §7b)                                               */
/* ------------------------------------------------------------------ */

/** Composed server-side. Only available once the audit is submitted. */
export const getReportModel = (id, opts = {}) =>
  read(`/audit/${id}/report`, {
    ...(opts.layout ? { layout: opts.layout } : {}),
    ...(opts.all ? { all: 1 } : {}),
    // Recommendations beside conformant rows. Off unless asked for — see the
    // composer for why the text comes from the framework rather than the row.
    ...(opts.conformantRecs ? { conformant_recs: 1 } : {}),
  });

export const getReportContents = (id) => read(`/audit/${id}/report/contents`);

/** Either { preset } or { ids, is_selected }. */
export const setReportContents = (id, body) =>
  mutate("post", `/audit/${id}/report/contents`, body);

export const saveReportSection = (id, key, body) =>
  mutate("put", `/audit/${id}/report/section/${key}`, { body });

/**
 * Open an issue and get back the model to draw (design §7b).
 *
 * The issue number is allocated server-side and stamped into the model's
 * header, because the document's own footer carries it — so the number has to
 * exist before anything renders, not after.
 */
export const openReportIssue = (id, body) =>
  mutate("post", `/audit/${id}/report/issue`, body);

export const listReportIssues = (id) => read(`/audit/${id}/report/issues`);
export const getReportIssue = (id, no) => read(`/audit/${id}/report/issues/${no}`);

/* ------------------------------------------------------------------ */
/* discarding a draft                                                  */
/* ------------------------------------------------------------------ */

/** What discarding would destroy, so the confirmation can be specific. */
/* ------------------------------------------------------------------ */
/* carry-forward on a repeat visit (design §8)                         */
/* ------------------------------------------------------------------ */

/*
 * Online only, and not by omission.
 *
 * Carrying reads the previous visit's answers, which live on the server and
 * were never downloaded to this device — the local store holds one audit, not
 * a site's history. Carrying is also a thing you do once, at the desk, before
 * going out; it is not fieldwork. So there is no offline path here and no
 * outbox entry: if the tablet cannot reach the server, the auditor starts
 * fresh, which is the safe direction to fail in.
 */
/* ------------------------------------------------------------------ */
/* trend — one site over time (design §12 item 11)                     */
/* ------------------------------------------------------------------ */

/*
 * Online only. A trend spans a site's whole history, which was never on this
 * device — the local store holds one audit. It is also read at a desk, not on
 * a plant floor.
 *
 * The site is identified by ids, never by name: matching a trend on the site
 * string splits the history in two the first time somebody retypes it
 * (decision 6).
 */
/**
 * One audit as a dashboard (§10): answer-state distribution per pillar, the
 * conformance percentage, the band and what the band asserts.
 *
 * Online only, like the trend. It reads the frozen snapshot and the whole
 * question set, neither of which the offline store holds for a finished audit.
 */
export const auditInsight = (id) => read(`/audit/${id}/insight`);

/**
 * Who can be nominated as a reviewer (§7c).
 *
 * Names and ids only — the server does not return email addresses, and the
 * picker does not need them.
 */
export const listPeople = (role) =>
  read("/people" + (role ? "?role=" + encodeURIComponent(role) : ""));

/* ------------------------------------------------------------------ */
/* framework administration (§7c) — Admin only, enforced server-side    */
/* ------------------------------------------------------------------ */

export const adminFramework = () => read("/admin/framework");

/* ---------------- publishing a version (§10a–§10c) ---------------- */

/**
 * The template, as a browser download.
 *
 * Fetched as a blob rather than linked directly: the route needs the
 * Authorization header, and a plain <a href> cannot send one.
 */
export async function adminFrameworkTemplate() {
  try {
    const r = await axios.get(BASE + "/admin/framework/template", { responseType: "blob" });
    return r.data;
  } catch (err) {
    throw wrap(err);
  }
}

/** Upload a workbook and stage it. Validation errors arrive as err.body.errors. */
export async function adminFrameworkUpload(file, version) {
  const form = new FormData();
  form.append("file", file, file.name || "framework.xlsx");
  form.append("version", version);
  try {
    const r = await axios.post(BASE + "/admin/framework/upload", form);
    return r.data;
  } catch (err) {
    throw wrap(err);
  }
}

/**
 * The three tables editable without a new version (§10d).
 * Any subset may be sent; validation errors arrive as err.body.errors.
 */
/* ---------------- scheduling (§7d) ---------------- */

/**
 * Asked before saving. A conflict found afterwards is a double-booking, not a
 * warning — which is why this is a separate call and not part of create.
 */
export const scheduleConflicts = (userId, start, end, excludeAuditId) =>
  read(
    "/schedule/conflicts?user_id=" + encodeURIComponent(userId) +
    "&start_date=" + encodeURIComponent(start) +
    "&end_date=" + encodeURIComponent(end) +
    (excludeAuditId ? "&exclude_audit_id=" + encodeURIComponent(excludeAuditId) : "")
  );

export const scheduleFor = (from, to) =>
  read("/schedule?from=" + encodeURIComponent(from) + "&to=" + encodeURIComponent(to));

export const scheduleLoad = (period, month) =>
  read("/schedule/load?period=" + encodeURIComponent(period) +
       (month ? "&month=" + encodeURIComponent(month) : ""));

/** take_from_conflicts says "yes, take them" — the server refuses without it. */
export const assignAudit = (id, userId, take) =>
  mutate("post", "/audit/" + id + "/assign", { user_id: userId, take_from_conflicts: !!take });

export const adminSaveConfig = (body) => mutate("put", "/admin/framework/config", body);

export const adminFrameworkPending = () => read("/admin/framework/pending");

/** decisions: { "P1-004": true } — true means "still the same question". */
export const adminFrameworkActivate = (version, decisions) =>
  mutate("post", "/admin/framework/activate", { version, decisions });

export const adminFrameworkDiscard = (version) =>
  mutate("delete", `/admin/framework/pending/${encodeURIComponent(version)}`);
export const adminUsers = () => read("/admin/users");
export const adminSetRoles = (id, roles) =>
  mutate("put", `/admin/users/${id}/roles`, { roles });

export const trendSites = () => read("/csa-trend/sites");
export const trend = (orgId, siteId) =>
  read(`/csa-trend?org_id=${encodeURIComponent(orgId)}&site_id=${encodeURIComponent(siteId)}`);

export const carryPreview = (id) => read(`/audit/${id}/carry-preview`);
export const carryForward = (id) => mutate("post", `/audit/${id}/carry`, {});

/** Confirm named questions, or every outstanding one with { all: true }. */
export const confirmCarried = (id, body) => mutate("post", `/audit/${id}/confirm`, body);

/** Last visit's answers, keyed by question code — the "last time: …" line. */
export const previousAnswers = (id) => read(`/audit/${id}/previous`);

export const previewDiscard = (id) => read(`/audit/${id}/discard-preview`);

/** Drafts only, owner or admin. The server enforces both. */
export const discardAudit = (id) => mutate("delete", `/audit/${id}`);

/* ---------- Tier 2 drafting (§11) ---------- */

/**
 * Start a drafting run. 202 and a job id — the work happens in the background,
 * so this returns long before there is anything to read.
 */
export const startDraft = (id) => mutate("post", `/audit/${id}/draft`, {});

/** Where the run has got to. Polled while it is running. */
export const draftProgress = (id) => read(`/audit/${id}/draft`);

/** Stop it. What was already drafted is kept. */
export const cancelDraft = (id) => mutate("delete", `/audit/${id}/draft`);

/** The drafts, disagreements first — the server orders them that way. */
export const draftItems = (id) => read(`/audit/${id}/draft/items`);

/**
 * Put a draft into the report.
 *
 * `{ question_code }` for one, `{ all: true }` for every draft the two passes
 * agreed on. Bulk never takes a flagged one. `overwrite` is required to
 * replace a sentence an auditor wrote by hand.
 */
export const acceptDraft = (id, body) => mutate("post", `/audit/${id}/draft/accept`, body);

/* ---------- Tier 3 capture assist (§8) ---------- */

/**
 * Tidy one note into one sentence, now, while the auditor is looking at it.
 *
 * Online only, and deliberately not queued. A suggestion that arrives an hour
 * later at a different machine is worse than none: the auditor has moved on,
 * and the one thing this tier is for — improving the note while the evidence
 * is still in front of them — has already been lost.
 *
 * Two shapes of answer, both 200. `usable: true` carries the sentence;
 * `usable: false` carries advice, because a note too thin to expand is the
 * most useful thing this can tell somebody who can still go and look.
 */
export const assist = (id, questionCode, note) =>
  mutate("post", `/audit/${id}/assist`, { question_code: questionCode, note });

/* ---------- one question over time (§10c) ---------- */

/** The questions this site has ever answered, for the picker. */
export const trendQuestions = (orgId, siteId) =>
  read(`/csa-trend/questions?org_id=${encodeURIComponent(orgId)}&site_id=${encodeURIComponent(siteId)}`);

/** One question's series. Points carry break_before where it stops comparing. */
export const questionTrend = (orgId, siteId, code) =>
  read(`/csa-trend/question?org_id=${encodeURIComponent(orgId)}` +
       `&site_id=${encodeURIComponent(siteId)}&code=${encodeURIComponent(code)}`);

/**
 * A photograph's bytes, through the API rather than from S3.
 *
 * The objects are public and CORS is open, and fetching them straight from
 * amazonaws.com still failed with "Failed to fetch" in a real browser — an
 * extension, a filter or a corporate proxy is enough, and none of it is ours
 * to fix. Going through the origin the app is already authenticated against
 * removes the question, and is the request a locked-down network has already
 * been told to allow.
 *
 * Uses axios so the Authorization header goes with it; the route is guarded by
 * the same ownership rule as everything else on the audit.
 */
export async function photoBytes(auditId, localId, size) {
  const r = await axios.get(
    `${BASE}/audit/${auditId}/photos/${encodeURIComponent(localId)}/bytes` +
      (size === "full" ? "?size=full" : ""),
    { responseType: "arraybuffer" }
  );
  return new Uint8Array(r.data);
}
